import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getAdvancedSettings, updateAdvancedSettings } from '@/services/settingsApi';

export const AdvancedSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [robotsTxt, setRobotsTxt] = useState('');
  const [sitemapXml, setSitemapXml] = useState('');
  const [llmsTxt, setLlmsTxt] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAdvancedSettings();
        const data = res?.data || res || {};
        setRobotsTxt(data.robots_txt || '');
        setSitemapXml(data.sitemap_xml || '');
        setLlmsTxt(data.llms_txt || '');
      } catch (error) {
        console.error(error);
        toast.error('Failed to load advanced settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdvancedSettings({
        robots_txt: robotsTxt,
        sitemap_xml: sitemapXml,
        llms_txt: llmsTxt,
      });
      toast.success('Advanced settings saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">robots.txt File</CardTitle>
          <p className="text-sm text-slate-600">Control search engine crawling and indexing</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current robots.txt Content</Label>
            <Textarea
              value={robotsTxt}
              onChange={(e) => setRobotsTxt(e.target.value)}
              rows={10}
              className="mt-2 font-mono text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept=".txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setRobotsTxt(event.target.result);
                    toast.success('robots.txt file loaded');
                  };
                  reader.readAsText(file);
                }
              }}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={() => {
                const blob = new Blob([robotsTxt], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'robots.txt';
                a.click();
              }}
            >
              Download
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">sitemap.xml File</CardTitle>
          <p className="text-sm text-slate-600">Help search engines discover your pages</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current sitemap.xml Content</Label>
            <Textarea
              value={sitemapXml}
              onChange={(e) => setSitemapXml(e.target.value)}
              rows={12}
              className="mt-2 font-mono text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept=".xml"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setSitemapXml(event.target.result);
                    toast.success('sitemap.xml file loaded');
                  };
                  reader.readAsText(file);
                }
              }}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={() => {
                const blob = new Blob([sitemapXml], { type: 'application/xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'sitemap.xml';
                a.click();
              }}
            >
              Download
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">llms.txt File</CardTitle>
          <p className="text-sm text-slate-600">Guidance for AI / LLM crawlers</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current llms.txt Content</Label>
            <Textarea
              value={llmsTxt}
              onChange={(e) => setLlmsTxt(e.target.value)}
              rows={8}
              className="mt-2 font-mono text-xs"
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Save Advanced Settings
      </Button>
    </div>
  );
};